import { useQuery } from "@tanstack/react-query";

import { queryKeys } from "@nasam/kernel";
import type { AssetTypesRepository } from "../domain";

/** The API's largest page: a company's types fit in one or two requests. */
const PAGE_SIZE = 100;

export interface UseAllAssetTypeIdsResult {
  /** Undefined until loaded (or when the query can't run). */
  readonly ids: readonly string[] | undefined;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Every active asset type that has assets, for an "All asset types" choice
 * -- the same types the options picker offers with `onlyWithAssets`, but all
 * of them rather than the pages loaded so far. The API takes a list of ids,
 * not an "all" flag, so the choice is sent (and recognised on a saved alert)
 * as this list.
 */
export function useAllAssetTypeIds(
  repository: AssetTypesRepository | undefined,
  accountId: string | undefined,
  { enabled = true }: { readonly enabled?: boolean } = {},
): UseAllAssetTypeIdsResult {
  const query = useQuery({
    queryKey: queryKeys.assetTypes.list({ all: true, accountId, onlyWithAssets: true }),
    queryFn: async () => {
      const ids: string[] = [];
      for (let page = 1; ; page += 1) {
        const result = await repository!.list({
          page,
          limit: PAGE_SIZE,
          onlyWithAssets: true,
          ...(accountId === undefined ? {} : { accountId }),
        });
        for (const assetType of result.data) {
          if (assetType.status === "ACTIVE") ids.push(assetType.id);
        }
        if (result.meta.currentPage >= result.meta.totalPages) return ids;
      }
    },
    enabled: enabled && repository !== undefined && typeof repository.list === "function",
  });

  return { ids: query.data, isLoading: query.isLoading, isError: query.isError };
}
